/**
 * FormPostButton — renders a hidden HTML form and auto-submits it to the
 * gateway endpoint (Denizbank or Netahsilat).
 *
 * The form is appended to `document.body`, submitted, then removed.
 * When `openInSameWindow` is false the form targets `_blank` so the gateway
 * opens in a new tab, matching SF `open_in_same_window = false` behaviour.
 */
import { useRef } from 'react'

interface FormPostButtonProps {
  actionUrl: string
  fields: Record<string, string>
  openInSameWindow: boolean
  /** Label shown on the trigger button. Defaults to "Pay Now". */
  label?: string
  /** Called immediately before the form is submitted. */
  onBeforeSubmit?: () => void
}

export function FormPostButton({
  actionUrl,
  fields,
  openInSameWindow,
  label = 'Pay Now',
  onBeforeSubmit,
}: FormPostButtonProps) {
  const submitted = useRef(false)

  function handleClick() {
    if (submitted.current) return
    submitted.current = true

    onBeforeSubmit?.()

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = actionUrl
    form.target = openInSameWindow ? '_self' : '_blank'
    form.style.display = 'none'

    Object.entries(fields).forEach(([fieldName, fieldValue]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = fieldName
      input.value = fieldValue
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
    // Remove the form after submission to avoid accumulating hidden forms (especially when targeting _blank).
    setTimeout(() => form.remove(), 0)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 min-h-[44px] min-w-[44px]"
    >
      {label}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Specialised wrappers (kept thin — just forward the gateway-specific label)
// ---------------------------------------------------------------------------

export function DenizbankPaymentButton(props: FormPostButtonProps) {
  return <FormPostButton {...props} label={props.label ?? 'Pay with Denizbank'} />
}

export function NetahsilatPaymentButton(props: FormPostButtonProps) {
  return <FormPostButton {...props} label={props.label ?? 'Pay with Netahsilat'} />
}
